import { API_BASE, api, ApiError } from "@/lib/apiClient";
import { getOrCreateGuestTabletDeviceToken } from "@/lib/guestTabletDeviceStorage";
import { guestTabletPublicFetch } from "@/lib/guestTabletPublicFetch";
import { tenantSlug } from "@/lib/tenantSlug";
import type {
  GuestTabletContent,
  GuestTabletDevice,
  GuestTabletDeviceStatus,
  GuestTabletMobileSession,
  GuestTabletSessionDisplay,
  GuestTabletWorkstation,
} from "@/types/guestTablet";

const TENANT_HEADER = "X-Tenant-Slug";
export const GUEST_TABLET_DEVICE_HEADER = "X-Guest-Tablet-Device-Token";

export const GUEST_TABLET_WORKSTATION_STORAGE_KEY = "reception_guest_tablet_workstation";

function publicHeaders(extra?: HeadersInit): HeadersInit {
  const token = getOrCreateGuestTabletDeviceToken();
  return {
    [TENANT_HEADER]: tenantSlug(),
    Accept: "application/json",
    [GUEST_TABLET_DEVICE_HEADER]: token,
    ...(extra as Record<string, string> | undefined),
  };
}

function buildPublicUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await guestTabletPublicFetch(buildPublicUrl, publicHeaders, path, init);
  } catch (err) {
    if (err instanceof ApiError) throw err;
    const message =
      err instanceof Error &&
      (err.message === "Load failed" || err.message === "Failed to fetch")
        ? "Verbindung zum Server fehlgeschlagen."
        : err instanceof Error
          ? err.message
          : "Netzwerkfehler";
    throw new ApiError(0, message);
  }
  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { detail?: string };
      if (typeof parsed.detail === "string") message = parsed.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }
  if (res.status === 204) return undefined as T;
  try {
    return (await res.json()) as T;
  } catch {
    throw new ApiError(
      res.status,
      "Ungültige Server-Antwort. Bitte prüfen Sie, ob die API erreichbar ist.",
    );
  }
}

export async function fetchGuestTabletSession(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}`,
  );
}

export async function fetchGuestMobileSession(token: string): Promise<GuestTabletMobileSession> {
  return publicFetch<GuestTabletMobileSession>(
    `/api/v1/public/guest-tablet/mobile/${encodeURIComponent(token)}`,
  );
}

export async function submitGuestTabletAcknowledgment(body: {
  booking_id: string;
  acknowledgment_type: string;
  guest_name?: string;
  signature_data?: Record<string, unknown>;
  workstation_code?: string;
}): Promise<void> {
  await publicFetch("/api/v1/public/guest-tablet/acknowledgments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function guestTabletEventsUrl(workstationCode: string): string {
  const token = encodeURIComponent(getOrCreateGuestTabletDeviceToken());
  const path = `/api/v1/public/guest-tablet/events/${encodeURIComponent(workstationCode)}?device_token=${token}`;
  return buildPublicUrl(path);
}

export async function registerGuestTabletDevice(
  workstationCode: string,
): Promise<GuestTabletDeviceStatus> {
  const device_token = getOrCreateGuestTabletDeviceToken();
  return publicFetch<GuestTabletDeviceStatus>("/api/v1/public/guest-tablet/devices/register", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      workstation_code: workstationCode,
      device_token,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 512) : undefined,
    }),
  });
}

export async function fetchGuestTabletDeviceStatus(
  workstationCode: string,
): Promise<GuestTabletDeviceStatus> {
  return publicFetch<GuestTabletDeviceStatus>(
    `/api/v1/public/guest-tablet/devices/status?workstation_code=${encodeURIComponent(workstationCode)}`,
  );
}

export async function approveGuestTabletDevice(deviceId: string): Promise<GuestTabletDevice> {
  return api.post<GuestTabletDevice>(`/api/v1/guest-tablet/devices/${deviceId}/approve`, {});
}

export async function revokeGuestTabletDevice(deviceId: string): Promise<GuestTabletDevice> {
  return api.post<GuestTabletDevice>(`/api/v1/guest-tablet/devices/${deviceId}/revoke`, {});
}

export async function deleteGuestTabletDevice(deviceId: string): Promise<void> {
  await api.del(`/api/v1/guest-tablet/devices/${deviceId}`);
}

export async function updateGuestTabletDeviceLabel(
  deviceId: string,
  label: string | null,
): Promise<GuestTabletDevice> {
  return api.patch<GuestTabletDevice>(`/api/v1/guest-tablet/devices/${deviceId}`, { label });
}

export async function fetchGuestTabletSessionStaff(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return api.get<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}`,
  );
}

export async function startGuestTabletDemoPublic(
  workstationCode: string,
  body: Partial<GuestTabletDemoStart> = {},
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}/demo`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function fetchGuestTabletWorkstations(): Promise<GuestTabletWorkstation[]> {
  return api.get<GuestTabletWorkstation[]>("/api/v1/guest-tablet/workstations");
}

export async function fetchGuestTabletWorkstationsPublic(): Promise<
  { code: string; label: string }[]
> {
  return publicFetch<{ code: string; label: string }[]>("/api/v1/public/guest-tablet/workstations");
}

export async function createGuestTabletWorkstation(body: {
  code: string;
  label: string;
  sort_order?: number;
}): Promise<GuestTabletWorkstation> {
  return api.post<GuestTabletWorkstation>("/api/v1/guest-tablet/workstations", body);
}

export async function updateGuestTabletWorkstation(
  workstationId: string,
  body: { label?: string; is_active?: boolean; sort_order?: number },
): Promise<GuestTabletWorkstation> {
  return api.patch<GuestTabletWorkstation>(
    `/api/v1/guest-tablet/workstations/${workstationId}`,
    body,
  );
}

export async function deactivateGuestTabletWorkstation(
  workstationId: string,
): Promise<GuestTabletWorkstation> {
  return api.del<GuestTabletWorkstation>(`/api/v1/guest-tablet/workstations/${workstationId}`);
}

export async function fetchGuestTabletContent(): Promise<GuestTabletContent> {
  return api.get<GuestTabletContent>("/api/v1/guest-tablet/content");
}

export async function updateGuestTabletContent(
  body: Partial<Pick<GuestTabletContent, "idle_screen">>,
): Promise<GuestTabletContent> {
  return api.patch<GuestTabletContent>("/api/v1/guest-tablet/content", body);
}

export async function advanceGuestTabletStep(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return confirmGuestTabletData(workstationCode);
}

export async function confirmGuestTabletData(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}/confirm-data`,
    { method: "POST" },
  );
}

export async function recordGuestTabletPayment(
  workstationCode: string,
  body: { payment_method: "cash" | "card"; amount?: string },
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}/payment`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

export async function confirmGuestTabletPayment(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return api.post<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}/confirm-payment`,
    {},
  );
}

export async function resetGuestTabletSessionPublic(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}/reset`,
    { method: "POST" },
  );
}

export async function selectGuestTabletPitch(
  workstationCode: string,
  pitchId: string,
): Promise<GuestTabletSessionDisplay> {
  return publicFetch<GuestTabletSessionDisplay>(
    `/api/v1/public/guest-tablet/session/${encodeURIComponent(workstationCode)}/select-pitch`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pitch_id: pitchId }),
    },
  );
}

export async function updateGuestTabletSession(
  workstationCode: string,
  body: {
    booking_id?: string | null;
    check_in_status?: string;
    is_demo_mode?: boolean;
    guest_display_step?: string;
    guest_invoice_id?: string;
    restart_check_in?: boolean;
    pitch_selection?: {
      candidate_pitch_ids: string[];
      assigned_pitch_id?: string | null;
      skip_review_confirmation?: boolean;
      guest_data_confirmed?: boolean;
    };
  },
): Promise<GuestTabletSessionDisplay> {
  return api.put<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}`,
    body,
  );
}

export async function startGuestTabletDemo(
  workstationCode: string,
  body: Partial<GuestTabletDemoStart> = {},
): Promise<GuestTabletSessionDisplay> {
  return api.post<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}/demo`,
    body,
  );
}

export async function clearGuestTabletSession(
  workstationCode: string,
): Promise<GuestTabletSessionDisplay> {
  return api.del<GuestTabletSessionDisplay>(
    `/api/v1/guest-tablet/sessions/${encodeURIComponent(workstationCode)}`,
  );
}

export type GuestTabletDemoStart = {
  guest_name?: string;
  arrival_date?: string;
  departure_date?: string;
  nights?: number;
  pitch_label?: string;
  pitch_category?: string;
  license_plate?: string;
  has_dog?: boolean;
  is_returning_guest?: boolean;
};
